import { useEffect, useRef, useState } from "react";
import { X, Star, ImagePlus, Loader2, Trash2 } from "lucide-react";
import api from "../lib/api";

/**
 * BusinessPhotosModal — manages photos of an EXISTING business.
 * Uses /api/businesses/{id}/photos endpoints (upload, list, delete, set cover).
 */
export default function BusinessPhotosModal({ business, onClose, onUpdated }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileInput = useRef(null);
  const MAX = 6;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${business.id}/photos`);
      setPhotos(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [business.id]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setUploading(true);
    try {
      let nextPhotos = [...photos];
      for (const f of files) {
        if (nextPhotos.length >= MAX) break;
        if (!f.type.startsWith("image/")) continue;
        const form = new FormData();
        form.append("file", f);
        const isFirst = nextPhotos.length === 0;
        const { data } = await api.post(
          `/businesses/${business.id}/photos?is_cover=${isFirst ? "true" : "false"}`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        nextPhotos.push(data);
      }
      setPhotos(nextPhotos);
      onUpdated?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDelete = async (photo) => {
    if (!confirm(`Supprimer cette photo ?`)) return;
    try {
      await api.delete(`/businesses/${business.id}/photos/${photo.id}`);
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const onSetCover = async (photo) => {
    try {
      await api.post(`/businesses/${business.id}/photos/${photo.id}/cover`);
      await load();
      onUpdated?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    }
  };

  const src = (url) => url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-2 md:p-6"
      onClick={onClose}
      data-testid="biz-photos-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <header className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold">Photos · {business.name}</h3>
            <p className="text-xs text-stone-500 mt-0.5">{photos.length}/{MAX} photo{photos.length > 1 ? "s" : ""} · La photo de couverture apparaît dans les résultats</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100" data-testid="biz-photos-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="biz-photos-grid">
              {photos.map((p) => (
                <div key={p.id} className="relative group rounded-xl overflow-hidden border border-stone-200 aspect-[4/3] bg-stone-100" data-testid={`biz-photo-${p.id}`}>
                  <img src={src(p.url)} alt="" className="w-full h-full object-cover" />
                  {p.is_cover && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lokaso-gold text-lokaso-dark text-xs font-bold">
                      <Star className="w-3 h-3" fill="currentColor" /> Couverture
                    </span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-2 gap-1.5">
                    {!p.is_cover && (
                      <button
                        type="button"
                        onClick={() => onSetCover(p)}
                        className="text-xs px-2 py-1 rounded-md bg-white text-lokaso-dark font-semibold"
                        data-testid={`biz-photo-set-cover-${p.id}`}
                      >
                        Définir couverture
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(p)}
                      className="ml-auto p-1.5 rounded-md bg-white text-lokaso-red"
                      data-testid={`biz-photo-delete-${p.id}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}

              {photos.length < MAX && (
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="rounded-xl border-2 border-dashed border-stone-300 hover:border-lokaso-green hover:bg-lokaso-green/5 aspect-[4/3] flex flex-col items-center justify-center gap-2 text-stone-500"
                  data-testid="biz-photo-add-btn"
                >
                  {uploading ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Envoi…</span></>
                  ) : (
                    <>
                      <ImagePlus className="w-6 h-6" />
                      <span className="text-sm font-medium">Ajouter</span>
                      <span className="text-xs text-stone-400">{photos.length}/{MAX}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          <input ref={fileInput} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="biz-photo-file-input" />
          {err && <p className="mt-3 text-sm text-lokaso-red" data-testid="biz-photos-error">{err}</p>}
          <p className="mt-4 text-xs text-stone-500">JPG, PNG ou WebP. Max {MAX} photos par entreprise. Vous pouvez changer la couverture à tout moment.</p>
        </div>

        <footer className="px-5 py-3 border-t border-stone-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-lokaso-green text-white font-semibold hover:brightness-95" data-testid="biz-photos-done">
            Terminé
          </button>
        </footer>
      </div>
    </div>
  );
}
